// Sprint 5.7 (D27) — circular indicator for `contextUsedPct`.
//
// Lives in the composer toolbar (right of model + effort chips). Replaces
// the horizontal bar that used to sit in the chat header. Mirrors Cursor /
// VS Code's circular context widget.
//
// Anatomy:
//   • 16×16 px SVG viewport.
//   • 14×14 px ring (1 px margin on every side).
//   • 1.5 px stroke.
//   • Empty arc (the track): `stroke-muted-foreground/45` — the `fg-faint`
//     role of `docs/ui/04-tokens.md` §12.1 — a full 360° circle.
//   • Filled arc: `stroke-dasharray` + `stroke-dashoffset`, starts at 12 o'clock,
//     sweeps clockwise, colored by threshold through Tailwind role classes
//     (never an inline `var(--…)`: campaign donut-dead-tokens, 2026-09-13 —
//     the legacy `--fg-*` tokens were removed in 48fa77a6 and an SVG stroke
//     with an unresolvable var() is silently not painted, so the ring was
//     invisible below 70 % for four months):
//       <70%   → stroke-muted-foreground   (calm, ignorable)
//       70-84% → stroke-status-warning     (amber — heads up)
//       ≥85%   → stroke-status-error       (rose — act soon)
//   • Numeric label to the right, 11 px JetBrains Mono, same role (`text-*`).
//
// Tooltip text is the raw token math (cite C10 in edge-cases):
//   "`used` / `max` tokens (`pct%`)" + the formula the backend uses to
//   derive it — the 0-100 clamp applied in `computeContextUsage` /
//   `contextUsageFromAssistant` (sprint context-donut-last-call, D013-EC-619:
//   the source is the last model response of the main agent, same
//   definition Claude Code publishes, not the turn-summed usage).
//
// Hidden (returns null) when `pct === null` — the UI surface simply folds
// away until the first `result` event arrives.
//
// Animation: 200 ms ease-out transition on stroke-dashoffset and stroke
// color so fill updates pulse rather than jump.

import type { CSSProperties } from 'react';
import { useTranslation } from '../i18n';

const SIZE = 16;
const RING = 14; // diameter
const STROKE = 1.5;
const RADIUS = (RING - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface ContextDonutProps {
  /** Percentage 0-100 or null to hide the component. */
  pct: number | null;
  /**
   * Optional override for the tooltip. Defaults to a generic explainer so
   * callers that only know the pct still get a usable tooltip. Pass a
   * longer string (e.g. `used/max tokens`) where you have that data.
   */
  tooltip?: string;
}

type Threshold = 'calm' | 'warning' | 'error';

function thresholdFor(pct: number): Threshold {
  if (pct >= 85) return 'error';
  if (pct >= 70) return 'warning';
  return 'calm';
}

const THRESHOLD_STROKE: Record<Threshold, string> = {
  calm: 'stroke-muted-foreground',
  warning: 'stroke-status-warning',
  error: 'stroke-status-error',
};
const THRESHOLD_TEXT: Record<Threshold, string> = {
  calm: 'text-muted-foreground',
  warning: 'text-status-warning',
  error: 'text-status-error',
};

export function ContextDonut({ pct, tooltip }: ContextDonutProps) {
  const { t } = useTranslation();
  if (pct === null || pct === undefined || !Number.isFinite(pct)) {
    return null;
  }

  // Defence in depth — the backend clamps this to 0-100 but fixtures and
  // future regressions can drift. Clamp here so the SVG math stays valid.
  const clamped = Math.max(0, Math.min(100, pct));
  const filled = thresholdFor(clamped);
  // The filled arc is painted by "erasing" from a fully-dashed stroke.
  // dashoffset = full - (used portion) → only the used portion shows.
  const dashOffset = CIRCUMFERENCE * (1 - clamped / 100);

  const resolvedTooltip =
    tooltip ??
    t('contextDonut.defaultTooltip', { pct: Math.round(clamped) });

  const animStyle: CSSProperties = {
    transition: 'stroke-dashoffset 200ms ease-out, stroke 200ms ease-out',
  };

  return (
    <span
      className="inline-flex items-center gap-1"
      aria-label={t('contextDonut.ariaLabel', { pct: Math.round(clamped) })}
      title={resolvedTooltip}
      data-testid="context-donut"
      data-threshold={filled}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        // Rotate -90° so the stroke starts at 12 o'clock instead of 3 o'clock.
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          className="stroke-muted-foreground/45"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          className={THRESHOLD_STROKE[filled]}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          style={animStyle}
        />
      </svg>
      <span className={`font-mono text-caption tabular-nums ${THRESHOLD_TEXT[filled]}`}>
        {Math.round(clamped)}%
      </span>
    </span>
  );
}
